import { Component, type ErrorInfo, type ReactNode } from 'react';
import { TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { StateView } from '@/components/ui/Feedback';
import { getBridge } from '@/platform';

/**
 * Граница ошибок приложения.
 *
 * Без неё исключение в любом экране размонтирует всё дерево, и в мессенджере
 * остаётся белый экран: ни объяснения, ни выхода — приложение можно только
 * закрыть. Здесь пользователь видит, что произошло, и может продолжить.
 *
 * Текст ошибки показывается намеренно: в WebView консоль недоступна, и без
 * него сообщение о поломке невозможно проверить. По той же причине рядом
 * печатается место падения: одного сообщения вроде «reading 'trim'» мало,
 * чтобы понять, какой экран сломался.
 */
interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  /** Первые кадры стека компонентов: по ним виден упавший экран. */
  where: string | null;
}

const SUPPORT_URL = (import.meta.env.VITE_SUPPORT_URL as string) || 'https://t.me/brand_doctor_support';

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, where: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, where: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Консоли в мессенджере нет, но при отладке через браузер запись помогает
    console.error('Экран упал:', error, info.componentStack);
    this.setState({ where: topFrames(info.componentStack) });
  }

  render() {
    const { error, where } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        className="mx-auto flex w-full max-w-[520px] flex-col justify-center px-5"
        style={{ minHeight: 'var(--bd-viewport-height)' }}
      >
        <StateView
          icon={<TriangleAlert className="h-6 w-6" aria-hidden />}
          title="Что-то сломалось"
          description="Приложение не смогло показать этот экран. Данные не потеряны — попробуйте открыть заново."
          primary={
            <Button variant="primary" block onClick={() => window.location.assign('/home')}>
              Вернуться на главную
            </Button>
          }
          secondary={
            <Button variant="ghost" block onClick={() => getBridge().openInternalLink(SUPPORT_URL)}>
              Написать в поддержку
            </Button>
          }
        >
          <p className="select-text rounded-control border border-line bg-surface-2 p-2 text-left text-[10px] leading-[1.4] text-muted">
            {error.message} · сборка {__BUILD_ID__}
            {where ? <span className="mt-1 block whitespace-pre-line">{where}</span> : null}
          </p>
        </StateView>
      </div>
    );
  }
}

/**
 * Верхние кадры стека компонентов.
 *
 * Полный стек в мессенджере не прочитать, а первые строки уже называют экран,
 * на котором всё сломалось, — этого достаточно, чтобы найти место в коде.
 */
function topFrames(stack: string | null | undefined, limit = 4): string | null {
  if (!stack) return null;

  const frames = stack
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, limit);

  return frames.length > 0 ? frames.join('\n') : null;
}
